'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { LogIn, LogOut, Shield, ShieldCheck, User, UserPlus } from 'lucide-react';
import { LIMITS, type MeResponse, type UserDto } from '@freealbum/shared';
import {
  Badge,
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from '@freealbum/ui';
import { authClient } from '@/auth/client';
import { SignInDialog, type AuthMode } from '@/components/auth/sign-in';
import { Link, useRouter } from '@/i18n/navigation';
import { formatBytes } from '@/lib/utils';

export function AuthMenu({ me, onChanged }: { me: MeResponse; onChanged?: () => void | Promise<unknown> }) {
  const t = useTranslations('auth');
  const tn = useTranslations('nav');
  const tc = useTranslations('common');
  const router = useRouter();
  const [dialog, setDialog] = React.useState(false);
  const [authMode, setAuthMode] = React.useState<AuthMode>('signIn');
  const [signingOut, setSigningOut] = React.useState(false);

  const openAuth = (mode: AuthMode) => {
    setAuthMode(mode);
    setDialog(true);
  };

  const signedIn = async () => {
    await onChanged?.();
    router.refresh();
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await authClient.signOut();
      await onChanged?.();
      // The home page's recent albums are rendered on the server for the signed-in user, so the
      // page is refreshed as well; the navigation bar covers that part.
      router.refresh();
    } catch {
      toast({ title: tc('actionFailed'), variant: 'error' });
    } finally {
      setSigningOut(false);
    }
  };

  // Who they are, and which sign-in methods exist, arrived with the page: the layout resolved the
  // session on the server, so this never shows "Guest" to someone who is signed in while a request
  // is still on its way. No spinner, and no "sign in" prompt as the resting state either. Everyone
  // is a guest until they choose otherwise: a visitor who has not started anything yet simply has
  // no row on the server, but they are a guest all the same, and the chrome should say so rather
  // than ask them to log into a product that never requires it. Signing in stays available inside
  // the menu. The quota shown is the real one they would get, so the number does not jump later.
  //
  // What the menu may NOT do is advertise an action the deployment cannot perform: with both
  // providers switched off, "sign in" used to open a dialog with nothing in it
  // (docs/10-ux-audit.md · UX-29).
  const { features } = me;
  const canSignIn = features.google || (features.emailPassword ?? features.devLogin);
  const user: UserDto = me.user ?? {
    id: '',
    name: '',
    email: '',
    isAnonymous: true,
    role: 'user',
    retentionUnlimited: false,
    storageUsedBytes: 0,
    storageQuotaBytes: LIMITS.userStorageBytes,
  };
  const label = user.isAnonymous ? t('guest') : user.name || user.email || t('account');

  return (
    <>
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label={t('account')} loading={signingOut} startIcon={<User aria-hidden className="size-4" />}>
            <span className="max-w-28 truncate">{label}</span>
            {user.isAnonymous ? <Badge variant="warning">{t('guestBadge')}</Badge> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium text-ink">{label}</p>
            {user.email ? (
              <p className="truncate text-xs text-ink-3" dir="ltr">
                {user.email}
              </p>
            ) : null}
            <p className="text-2xs mt-1 text-ink-3">
              {t('storageUsed', { used: formatBytes(user.storageUsedBytes), quota: formatBytes(user.storageQuotaBytes) })}
            </p>
          </div>
          <DropdownMenuSeparator />
          {user.isAnonymous ? (
            canSignIn ? (
              <>
                <p className="px-2.5 py-2 text-xs text-ink-2">{t('guestExplain')}</p>
                <DropdownMenuItem icon={<LogIn aria-hidden />} onSelect={() => openAuth('signIn')}>
                  {t('signIn')}
                </DropdownMenuItem>
                {features?.emailRegistration ? (
                  <DropdownMenuItem icon={<UserPlus aria-hidden />} onSelect={() => openAuth('register')}>
                    {t('createAccount')}
                  </DropdownMenuItem>
                ) : null}
              </>
            ) : (
              // No provider is configured. Say so once, then give the guest the thing that actually
              // protects their work here: a backup of each album, from the library.
              <>
                <p className="px-2.5 py-2 text-xs text-ink-2">
                  <span className="font-medium text-ink">{t('signInUnavailable')}</span>
                  <br />
                  {t('signInUnavailableBody')}
                </p>
                <DropdownMenuItem asChild icon={<ShieldCheck aria-hidden />}>
                  <Link href="/albums">{t('guestBackupCta')}</Link>
                </DropdownMenuItem>
              </>
            )
          ) : null}
          {user.role === 'admin' ? (
            <DropdownMenuItem asChild icon={<Shield className="size-4 text-ink-3" />}>
              <Link href="/admin">{tn('admin')}</Link>
            </DropdownMenuItem>
          ) : null}
          {!user.isAnonymous ? (
            <DropdownMenuItem
              icon={<LogOut className="size-4" />}
              onSelect={() => void signOut()}
            >
              {t('signOut')}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenuRoot>
      {/* Mounted only when there is a method to offer, so no code path can open an empty dialog. */}
      {canSignIn ? (
        <SignInDialog
          open={dialog}
          onOpenChange={setDialog}
          features={features}
          initialMode={authMode}
          onSignedIn={signedIn}
        />
      ) : null}
    </>
  );
}
